import type { LegalBlock, PublishedLegalDocument } from "../content/content-types.ts"

type LegalDocumentProps = {
  readonly document: PublishedLegalDocument
}

const dateFormatter = new Intl.DateTimeFormat("ko-KR", { day: "numeric", month: "long", timeZone: "UTC", year: "numeric" })

const assertNever = (value: never): never => {
  throw new TypeError(`Unsupported legal block: ${String(value)}`)
}

const renderBlock = (block: LegalBlock, blockId: string) => {
  switch (block.kind) {
    case "paragraph":
      return <p>{block.text}</p>
    case "ordered-list":
      return (
        <ol>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      )
    case "unordered-list":
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )
    case "table": {
      const hintId = `legal-table-hint-${blockId}`
      return (
        <div className="legal-table">
          <p className="legal-table-hint" id={hintId}>
            표 전체를 보려면 좌우로 스크롤하세요.
          </p>
          {/* biome-ignore lint/a11y/noNoninteractiveTabindex: Labeled overflow tables require keyboard scrolling at narrow widths. */}
          <section aria-describedby={hintId} aria-label={block.label} className="legal-table-region" tabIndex={0}>
            <table aria-label={block.label}>
              <thead>
                <tr>
                  {block.columns.map((column) => (
                    <th key={column} scope="col">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.join("|")}>
                    {row.map((cell, index) =>
                      index === 0 ? (
                        <th key={`${block.columns[index] ?? "cell"}-${cell}`} scope="row">
                          {cell}
                        </th>
                      ) : (
                        <td key={`${block.columns[index] ?? "cell"}-${cell}`}>{cell}</td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      )
    }
    default:
      return assertNever(block)
  }
}

export const LegalDocumentContent = ({ document }: LegalDocumentProps) => (
  <article aria-label={document.title} className="legal-document" data-publishable="true">
    <dl aria-label="문서 시행 정보" className="legal-document-meta">
      <div>
        <dt>공고일</dt>
        <dd>
          <time dateTime={document.announcedAt}>{dateFormatter.format(new Date(`${document.announcedAt}T00:00:00Z`))}</time>
        </dd>
      </div>
      <div>
        <dt>시행일</dt>
        <dd>
          <time dateTime={document.effectiveAt}>{dateFormatter.format(new Date(`${document.effectiveAt}T00:00:00Z`))}</time>
        </dd>
      </div>
    </dl>
    {document.preamble.map((block, index) => (
      <div className="legal-preamble" key={`${block.kind}-${index}`}>
        {renderBlock(block, `${document.slug}-preamble-${index}`)}
      </div>
    ))}
    {document.sections.map((section, sectionIndex) => (
      <section className="legal-section" key={section.title}>
        <h2>{section.title}</h2>
        {section.blocks.map((block, blockIndex) => (
          <div className="legal-block" key={`${block.kind}-${blockIndex}`}>
            {renderBlock(block, `${document.slug}-${sectionIndex}-${blockIndex}`)}
          </div>
        ))}
      </section>
    ))}
  </article>
)
